import React, { Fragment, useState } from 'react';
import { Col, Form, Input, Row } from 'antd';
import FormListAddButton from '../../FormListAddButton';
import { PricesPerItem } from '../../../../../server/src/models';
import FormListRemoveButton from '../../FormListRemoveButton';
import FeatureStatusInput from '../../Shared/Inputs/FeatureStatusInput';

const cardHeaderTitleStyle = { fontSize: '18px' };
const cardSubHeaderTitleStyle = { fontSize: '16px', fontStyle: 'italic' };

interface PricePerItemListProps {
    isDetailMode: boolean;
    pricesPerItem: PricesPerItem | undefined;
}

const PricePerItemList: React.FC<PricePerItemListProps> = ({ isDetailMode, pricesPerItem }: PricePerItemListProps) => {
    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(false);
    return (
        <>
            <div className="section-price-per-item card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Price per item
                </p>
                <div className="d-inline-flex align-items-center">
                    <span className="mr-2 d-none d-sm-block">Feature is:</span>
                    <FeatureStatusInput
                        name={['pricePerItem', 'enabled']}
                        id="price_per_item_enabled"
                        isDetailMode={isDetailMode}
                        enabled={pricesPerItem?.enabled}
                        isFeatureDisabled={isFeatureDisabled}
                        onChange={(value): void => {
                            setIsFeatureDisabled(!value);
                        }}
                    />
                </div>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={23} offset={1}>
                        <p className="m-0 p-0 h5" style={cardSubHeaderTitleStyle}>
                            Articles prices
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col span={23} offset={1}>
                        <Form.List name={['pricePerItem', 'items']}>
                            {(items, { add: newItem, remove: removeItem }): JSX.Element => (
                                <>
                                    {items.map((item) => (
                                        <Fragment key={item.key}>
                                            <Row>
                                                <Col span={10}>
                                                    <Form.Item
                                                        name={[item.name, 'name']}
                                                        label={isDetailMode ? '' : 'Article'}
                                                        style={{ fontWeight: 'bold' }}
                                                        rules={[
                                                            {
                                                                required: !isDetailMode,
                                                                message: 'Name is required',
                                                            },
                                                        ]}
                                                    >
                                                        {isDetailMode ? (
                                                            <span>{pricesPerItem?.items?.[item.key]?.name}</span>
                                                        ) : (
                                                            <Input title="Article's name" />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col span={6}>
                                                    <Form.Item
                                                        labelCol={{ span: 12 }}
                                                        name={[item.name, 'price']}
                                                        label={isDetailMode ? '' : 'Price'}
                                                        style={{ fontWeight: 'bold' }}
                                                        rules={[
                                                            {
                                                                required: !isDetailMode,
                                                                message: 'Price is required',
                                                            },
                                                        ]}
                                                    >
                                                        {isDetailMode ? (
                                                            <span>{pricesPerItem?.items?.[item.key]?.price}</span>
                                                        ) : (
                                                            <Input type="number" title="Article's price" />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col span={6}>
                                                    <Form.Item
                                                        labelCol={{ span: 12 }}
                                                        name={[item.name, 'nextDayPrice']}
                                                        label={isDetailMode ? '' : 'Next day price'}
                                                        style={{ fontWeight: 'bold' }}
                                                        rules={[
                                                            {
                                                                required: !isDetailMode,
                                                                message: 'Next day price is required',
                                                            },
                                                        ]}
                                                    >
                                                        {isDetailMode ? (
                                                            <span>
                                                                {pricesPerItem?.items?.[item.key]?.nextDayPrice}
                                                            </span>
                                                        ) : (
                                                            <Input
                                                                type="number"
                                                                title="Price for next day delivery on the specified item"
                                                            />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col span={1} offset={1}>
                                                    <FormListRemoveButton
                                                        isDetailMode={isDetailMode}
                                                        remove={removeItem}
                                                        name={item.name}
                                                    />
                                                </Col>
                                            </Row>
                                        </Fragment>
                                    ))}
                                    <FormListAddButton
                                        id="add-item-price"
                                        add={newItem}
                                        isDetailMode={isDetailMode}
                                        text="Add an article price"
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={23} offset={1}>
                        <p className="m-0 p-0 h5" style={cardSubHeaderTitleStyle}>
                            Additional item price modifications
                        </p>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={5} offset={1}>
                        <Form.Item
                            name={['pricePerItem', 'pricePercentagePerAdditionalItem']}
                            label="Discount (%)"
                            style={{ fontWeight: 'bold' }}
                        >
                            {isDetailMode ? (
                                <span>{pricesPerItem?.pricePercentagePerAdditionalItem}</span>
                            ) : (
                                <Input title="Additional item discount" />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={5} offset={1}>
                        <Form.Item
                            name={['pricePerItem', 'fixPricePerAdditionalItem']}
                            label="Fixed price (€)"
                            style={{ fontWeight: 'bold' }}
                        >
                            {isDetailMode ? (
                                <span>{pricesPerItem?.fixPricePerAdditionalItem}</span>
                            ) : (
                                <Input title="Fixed price for additional items" />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={5} offset={1}>
                        <Form.Item
                            name={['pricePerItem', 'maxFixPriceItems']}
                            label="Max additional items"
                            style={{ fontWeight: 'bold' }}
                        >
                            {isDetailMode ? (
                                <span>{pricesPerItem?.maxFixPriceItems}</span>
                            ) : (
                                <Input title="Maximum number of items set to fixed price" />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={5} offset={1}>
                        <Form.Item
                            name={['pricePerItem', 'fixFeePerAdditionalItem']}
                            label="Additional fee (€)"
                            style={{ fontWeight: 'bold' }}
                        >
                            {isDetailMode ? (
                                <span>{pricesPerItem?.fixFeePerAdditionalItem}</span>
                            ) : (
                                <Input title="Additional item fee" />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default PricePerItemList;
